/**
 * @author        patrick@zumper.com (Patrick Giddings)
 * @copyright     Copyright (c) 2023, Zumper
 */

import {
  DEFAULT_SORT_OPTION,
  NEWEST_SORT_OPTION,
  PRICE_SORT_ASC_OPTION,
  PRICE_SORT_DESC_OPTION,
  RATING_SORT_ASC_OPTION,
  RATING_SORT_DESC_OPTION,
} from '@zcode-ui/filters'
import { boxToQueryBounds, getDistanceFromCenter } from '@zcode/nr/es/shared/utils/location'
import type { BoundsObj, Listable, Listables, NearbyListablesInner } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import { logger } from '../../../../../logger'

type Sort =
  | typeof DEFAULT_SORT_OPTION
  | typeof NEWEST_SORT_OPTION
  | typeof PRICE_SORT_ASC_OPTION
  | typeof PRICE_SORT_DESC_OPTION
  | typeof RATING_SORT_ASC_OPTION
  | typeof RATING_SORT_DESC_OPTION

type PartialListable = Pick<Listable, 'listedOn' | 'maxPrice' | 'minPrice' | 'rating'>

export const createListablesSorter = (sort: Sort) => (l1: PartialListable, l2: PartialListable) => {
  switch (sort) {
    case NEWEST_SORT_OPTION:
      return (l2.listedOn ?? 0) - (l1.listedOn ?? 0)
    case PRICE_SORT_ASC_OPTION:
      return (l1.minPrice ?? l1.maxPrice ?? 0) - (l2.minPrice ?? l2.maxPrice ?? 0)
    case PRICE_SORT_DESC_OPTION:
      return (l2.maxPrice ?? l2.minPrice ?? 0) - (l1.maxPrice ?? l1.minPrice ?? 0)
    case RATING_SORT_ASC_OPTION:
      return (l1.rating ?? 0) - (l2.rating ?? 0)
    case RATING_SORT_DESC_OPTION:
      return (l2.rating ?? 0) - (l1.rating ?? 0)
    default:
      // maintain api sort
      return 0
  }
}

function isBoundsObject(box: NearbyListablesInner['box']): box is BoundsObj {
  return typeof box === 'object' && !Array.isArray(box)
}

export type PartialListables = Pick<
  Listables,
  'isLowResults' | 'listables' | 'listingCount' | 'nearby' | 'matching' | 'maxBox' | 'query'
>

export const transformLowResultsListablesResponse = <L extends PartialListables>(
  listables: L,
  dedupedListables: string[],
  additionalCount: number,
  sort?: Sort
): L => {
  if (listables.nearby?.matches?.listables?.length) {
    try {
      const allListables = [...listables.listables, ...listables.nearby.matches.listables]

      const matching = dedupedListables.length + listables.nearby.matches.listables.length

      const maxBox = allListables.reduce(
        ({ maxLat, maxLng, minLat, minLng }, listable) => {
          return {
            maxLat: Math.max(maxLat, listable.lat ?? Number.NEGATIVE_INFINITY),
            maxLng: Math.max(maxLng, listable.lng ?? Number.NEGATIVE_INFINITY),
            minLat: Math.min(minLat, listable.lat ?? Number.POSITIVE_INFINITY),
            minLng: Math.min(minLng, listable.lng ?? Number.POSITIVE_INFINITY),
          }
        },
        {
          maxLat: Number.NEGATIVE_INFINITY,
          maxLng: Number.NEGATIVE_INFINITY,
          minLat: Number.POSITIVE_INFINITY,
          minLng: Number.POSITIVE_INFINITY,
        }
      )
      const isMaxBoxValid = Object.values(maxBox).every(
        (v) => Math.abs(v) !== Number.POSITIVE_INFINITY
      )
      if (isMaxBoxValid) {
        maxBox.maxLat += 0.01
        maxBox.maxLng += 0.01
        maxBox.minLat -= 0.01
        maxBox.minLng -= 0.01
      }

      if (sort && sort !== DEFAULT_SORT_OPTION) {
        const listablesSorter = createListablesSorter(sort)
        return {
          ...listables,
          isLowResults: true,
          listables: allListables.sort(listablesSorter),
          listingCount: matching,
          matching,
          maxBox: isMaxBoxValid ? maxBox : null,
          nearby: {
            ...listables.nearby,
            matches: {
              ...listables.nearby.matches,
              listables: [],
            },
          },
        } satisfies L
      }
      const box = isBoundsObject(listables.nearby.matches.box)
        ? listables.nearby.matches.box
        : boxToQueryBounds(listables.nearby.matches.box)
      const center = {
        lat: (box.minLat + box.maxLat) / 2,
        lng: (box.minLng + box.maxLng) / 2,
      }
      const boxSize = listables.query?.box
        ? getDistanceFromCenter(center, {
            lat: listables.query.box.maxBox,
            lng: listables.query.box.maxLng,
          })
        : null
      const distanceThreshold =
        listables.query?.neighborhood || (boxSize !== null && boxSize <= 5) ? 1 : 5
      const { coreListables, nearbyListables } = listables.nearby.matches.listables.reduce(
        ({ coreListables, nearbyListables }, listable) => {
          const distance = getDistanceFromCenter(center, listable)
          listable._data.distanceFromSearchCenter = distance
          if (isNumber(distance) && distance <= distanceThreshold) {
            coreListables.push(listable)
          } else {
            nearbyListables.push(listable)
          }
          return {
            coreListables,
            nearbyListables,
          }
        },
        { coreListables: [...listables.listables], nearbyListables: [] as Listable[] }
      )
      return {
        ...listables,
        isLowResults: true,
        listables: coreListables,
        listingCount: matching,
        matching,
        maxBox: isMaxBoxValid ? maxBox : null,
        nearby: {
          ...listables.nearby,
          matches: {
            ...listables.nearby.matches,
            listables: nearbyListables,
          },
        },
      } satisfies L
    } catch (e) {
      logger.error('Failed to transform low results listables response', e)
    }
  }

  return {
    ...listables,
    matching: listables.matching + additionalCount,
    isLowResults: false,
    maxBox: null,
  } satisfies L
}
